import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import useDashboardApi from './useDashboardApi';
import { useContactsApi } from '@features/contacts';
import { logError } from '@lib/logger/logError';
import { logWarning } from '@lib/logger/logWarning';
import type { ReportType } from '../components/ReportDownloadModal';
import type {
  SessionDetails,
  SessionDetailsCall,
  SessionStats,
  SessionRecordings,
  LoadingState,
  BuildMetrics,
  DispositionMetrics,
} from '../types/dashboard.types';
import { useTranslation } from 'react-i18next';

export type CallsFilter = 'all' | 'connected' | 'meetings' | 'recordings';

const DEFAULT_PAGE_SIZE = 500;

export interface UseSessionDetailsViewModelReturn {
  // Data
  sessionDetails: SessionDetails | null;
  filteredCalls: SessionDetailsCall[];
  stats: SessionStats;
  selectedRecordings: SessionRecordings | null;
  totalContacts: number | null;
  flaggedContactsCount: number | null;

  // Filters
  callsFilter: CallsFilter;
  setCallsFilter: (filter: CallsFilter) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  dispositionFilter: string;
  setDispositionFilter: (disposition: string) => void;
  agentFilter: string;
  setAgentFilter: (agent: string) => void;
  actionFilter: string;
  setActionFilter: (action: string) => void;
  setOnClickFun: (type: string, filter: string) => void;

  // Loading
  loadingState: LoadingState;
  recordingsLoadingState: LoadingState;

  // Computed
  uniqueDispositions: string[];
  uniqueAgents: Array<{ id: string; name: string }>;
  uniqueActions: string[];

  // Actions
  loadRecordings: (callId: string) => Promise<void>;
  closeRecordings: () => void;
  goBack: () => void;
  downloadReport: (type: ReportType) => Promise<void>;
  isDownloadingReport: boolean;
}

// Compute stats from calls array (fallback when metrics endpoints are unavailable)
function computeStatsFromCalls (calls: SessionDetailsCall[]): SessionStats {
  const totalCalls = calls.length;
  const answered = calls.filter((c) => c.action === 'answered').length;
  const transferred = calls.filter((c) => c.action === 'transferred').length;
  const dialToConnectRatio = totalCalls > 0 ? (transferred / totalCalls) * 100 : 0;

  return {
    totalCalls,
    answered,
    transferred,
    dialToConnectRatio,
    gatekeeperConversation: calls.filter((c) => c.disposition === 'gatekeeper_conversation').length,
    leftVoicemail: calls.filter((c) => c.action === 'voicemail' || c.disposition === 'left_vm' || c.disposition === 'LVM').length,
    wrongNumber: calls.filter((c) => c.disposition === 'wrong_number').length,
    phoneTreeNoConnection: calls.filter((c) => c.disposition === 'phone_tree_no_connection').length,
    noLongerWithCompany: calls.filter((c) => c.disposition === 'no_longer_with_company').length,
    referredAnotherContact: calls.filter((c) => c.disposition === 'referred_another_contact').length,
    decisionMakerConversation: calls.filter((c) => c.disposition === 'decision_maker_conversation').length,
    optOut: calls.filter((c) => c.disposition === 'opt_out').length,
    noAnswer: calls.filter((c) => c.action === 'not_answered' || c.disposition === 'no_answer').length,
    badNumberBusySignal: calls.filter((c) => c.disposition === 'bad_number_busy_signal').length,
    phoneRefusal: calls.filter((c) => c.disposition === 'phone_refusal').length,
    refuseTransfer: calls.filter((c) => c.disposition === 'refuse_transfer').length,
    setAppointments: calls.filter((c) => c.disposition === 'set_appointment').length,
    withRecording: calls.filter((c) => c.existRecordings).length,
  };
}

// Map backend metrics to frontend SessionStats type
function mapBackendMetricsToStats (
  buildMetrics: BuildMetrics,
  dispositionMetrics: DispositionMetrics,
  calls: SessionDetailsCall[]
): SessionStats {
  const totalCalls = calls.length;
  const transferred = buildMetrics.transferred;
  const dialToConnectRatio = totalCalls > 0 ? (transferred / totalCalls) * 100 : 0;

  // Count recordings from calls array (not available in backend metrics)
  const withRecording = calls.filter((c) => c.existRecordings).length;

  return {
    totalCalls,
    answered: buildMetrics.answered,
    transferred,
    dialToConnectRatio,
    gatekeeperConversation: dispositionMetrics.gatekeeper_conversation,
    // Build metrics tracks voicemail as an action; disposition metrics tracks left_vm as a disposition.
    // These overlap but may differ in count, so we take the higher value to avoid under-reporting.
    leftVoicemail: Math.max(dispositionMetrics.left_vm, buildMetrics.voicemail),
    wrongNumber: dispositionMetrics.wrong_number,
    phoneTreeNoConnection: dispositionMetrics.phone_tree_no_connection,
    noLongerWithCompany: dispositionMetrics.no_longer_with_company,
    referredAnotherContact: dispositionMetrics.referred_another_contact,
    decisionMakerConversation: dispositionMetrics.decision_maker_conversation,
    optOut: dispositionMetrics.opt_out,
    // Same overlap: build metrics has not_answered (action), disposition metrics has no_answer (disposition).
    noAnswer: Math.max(dispositionMetrics.no_answer, buildMetrics.not_answered),
    badNumberBusySignal: dispositionMetrics.bad_number_busy_signal,
    phoneRefusal: dispositionMetrics.phone_refusal,
    refuseTransfer: dispositionMetrics.refuse_transfer,
    setAppointments: dispositionMetrics.set_appointment,
    withRecording,
  };
}

export default function useSessionDetailsViewModel (): UseSessionDetailsViewModelReturn {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const dashboardApi = useDashboardApi();
  const contactsApi = useContactsApi();
  const { currentUser } = useSessionStore();
  const currentUserId = currentUser?.userId ?? '';
  const { t } = useTranslation();

  // Data state
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [selectedRecordings, setSelectedRecordings] = useState<SessionRecordings | null>(null);
  const [backendStats, setBackendStats] = useState<SessionStats | null>(null);

  // Filter state
  const [callsFilter, setCallsFilter] = useState<CallsFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dispositionFilter, setDispositionFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  // Loading state
  const [loadingState, setLoadingState] = useState<LoadingState>('IDLE');
  const [recordingsLoadingState, setRecordingsLoadingState] = useState<LoadingState>('IDLE');
  const latestRecordingCallIdRef = useRef<string | null>(null);
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [contactListCount, setContactListCount] = useState<number | null>(null);
  const [flaggedContactsCount, setFlaggedContactsCount] = useState<number | null>(null);

  const fetchContactListCount = useCallback(async (contactListId: string) => {
    try {
      const contactsResponse = await contactsApi.getContactsByContactListIdAsync(contactListId);
      if (contactsResponse.success && Array.isArray(contactsResponse.data)) {
        setContactListCount(contactsResponse.data.length);

        const flaggedCount = contactsResponse.data.filter((contact) => contact.flagged === true).length;
        setFlaggedContactsCount(flaggedCount);
      }
    } catch (error) {
      await logError('Error fetching list count', 'useSessionDetailsViewModel', { error }, false);
    }
  }, [contactsApi]);

  // Fetch session details and metrics in parallel
  const fetchSessionDetails = useCallback(async () => {
    if (sessionId === undefined || currentUserId === '') {
      return;
    }

    setLoadingState('LOADING');
    try {
      // Fetch first page
      const firstPageResponse = await dashboardApi.getSessionDetailsAsync(
        sessionId,
        currentUserId,
        1,
        DEFAULT_PAGE_SIZE
      );
      
      if (!firstPageResponse.success) {
        const errorMessage = firstPageResponse.error?.raw ?? '';

        if (errorMessage.includes('404')) {
          setLoadingState('ERROR');
          void logWarning( t('dashboard.useSessionDetailsViewModel.withoutCalls', 'Call session without calls. No details available.'),
            'useSessionDetailsViewModel',
            { sessionId, error: firstPageResponse.error },
            false
          );

          return;
        }
        setLoadingState('ERROR');
        throw new Error(firstPageResponse.error?.message ?? 'Failed to load session details');
      }

      // API returns { data: { session, calls } } — extract safely from paginated wrapper
      const rawData = firstPageResponse.data?.data;
      const firstPageData: SessionDetails | null = Array.isArray(rawData)
        ? (rawData[0] ?? null)
        : (rawData as SessionDetails | undefined) ?? null;

      if (firstPageData === null) {
        void logWarning('Session details response succeeded but data resolved to null', 'useSessionDetailsViewModel', { sessionId, rawDataType: typeof rawData }, false);
      }

      if (firstPageData?.session == null) {
        setLoadingState('ERROR');
        void logError('Session info is missing', 'useSessionDetailsViewModel', { sessionId }, false);
        return;
      }

      const totalPages = Number(firstPageResponse.data?.totalPages) | 1;
      let allCalls = [...(firstPageData?.calls ?? [])];
      let currentPage = 1;
      
      while (currentPage < totalPages) {
        currentPage++;
        
        try {
          const pageResponse = await dashboardApi.getSessionDetailsAsync(
            sessionId,
            currentUserId,
            currentPage,
            DEFAULT_PAGE_SIZE
          );
          
          if (pageResponse.success) {
            const pageRawData = pageResponse.data?.data;
            const pageData: SessionDetails | null = Array.isArray(pageRawData) 
              ? (pageRawData[0] ?? null)
              : (pageRawData as SessionDetails | undefined) ?? null;
              
            if (((pageData?.calls) != null) && pageData?.calls.length > 0) {
              allCalls = [...allCalls, ...pageData.calls];
            } else {
              await logError('Error getting pages and calls', 'useSessionDetailsViewModel', { pageRawData }, false);
              break;
            }
          }
        } catch (error) {
          void logError(`Error fetching page ${currentPage}`, 'useSessionDetailsViewModel', { sessionId, error }, false);
        }
      }

      const completeSessionDetails: SessionDetails = {
        ...firstPageData,
        calls: allCalls,
      };
      setSessionDetails(completeSessionDetails);

      const listIdFromCall = allCalls[0]?.contactListId;

      if (listIdFromCall !== '') {
        void fetchContactListCount(listIdFromCall);
      }

      const [buildMetricsResponse, dispositionMetricsResponse] = await Promise.all([
        dashboardApi.getBuildMetricsAsync(sessionId),
        dashboardApi.getDispositionMetricsAsync(sessionId),
      ]);

      // Map backend metrics to frontend stats if available, otherwise fall back to call-derived stats
      if (buildMetricsResponse.success && dispositionMetricsResponse.success &&
          buildMetricsResponse.data !== undefined && dispositionMetricsResponse.data !== undefined) {
        const stats = mapBackendMetricsToStats(
          buildMetricsResponse.data,
          dispositionMetricsResponse.data,
          allCalls
        );
        setBackendStats(stats);
      } else {
        // Fallback: compute stats from calls when metrics endpoints are unavailable
        setBackendStats(allCalls.length > 0 ? computeStatsFromCalls(allCalls) : null);
      }

      setLoadingState('SUCCESS');
    } catch (error) {
      setLoadingState('ERROR');
      void logError('Failed to load session details', 'useSessionDetailsViewModel', { error, sessionId }, false);
    }
  }, [sessionId, currentUserId, dashboardApi, fetchContactListCount]);

  const totalContacts = useMemo(() => {
    if (loadingState === 'LOADING' || loadingState === 'IDLE' || (sessionDetails == null)) {
      return null;
    }
    const sessionCountFromDetails = (sessionDetails?.session)?.totalContacts;
    const sessionCount = contactListCount ?? null;

    if (sessionCountFromDetails !== undefined && sessionCountFromDetails !== null && sessionCountFromDetails !== 0) {
      return sessionCountFromDetails;
    }

    if (sessionCount === null) {
      return null;
    }
    
    return sessionCount ?? null;
  }, [contactListCount, sessionDetails, loadingState]);

  const setOnClickFun = (type: string, filter: string): void => {
    if (type === 'disposition') {
      setDispositionFilter(filter);
    } else {
      setActionFilter(filter);
    }
  };

  // Load on mount
  useEffect(() => {
    void fetchSessionDetails();
  }, [fetchSessionDetails]);

  // Use backend/fallback stats, with zeroed default as last resort
  const stats = useMemo((): SessionStats => {
    if (backendStats !== null) {
      return backendStats;
    }

    return {
      totalCalls: 0,
      answered: 0,
      transferred: 0,
      dialToConnectRatio: 0,
      gatekeeperConversation: 0,
      leftVoicemail: 0,
      wrongNumber: 0,
      phoneTreeNoConnection: 0,
      noLongerWithCompany: 0,
      referredAnotherContact: 0,
      decisionMakerConversation: 0,
      optOut: 0,
      noAnswer: 0,
      badNumberBusySignal: 0,
      phoneRefusal: 0,
      refuseTransfer: 0,
      setAppointments: 0,
      withRecording: 0,
    };
  }, [backendStats]);

  // Get unique dispositions for filter
  const uniqueDispositions = useMemo(() => {
    if (sessionDetails?.calls === undefined || sessionDetails.calls === null) {
      return [];
    }
    const dispositions = new Set<string>();
    sessionDetails.calls.forEach((call) => {
      if (call.disposition != null) {
        dispositions.add(call.disposition);
      }
    });
    return Array.from(dispositions).sort();
  }, [sessionDetails?.calls]);

  // Get unique agents for filter
  const uniqueAgents = useMemo(() => {
    if (sessionDetails?.calls === undefined || sessionDetails.calls === null) {
      return [];
    }
    const agentsMap = new Map<string, string>();
    sessionDetails.calls.forEach((call) => {
      if (call.agentId !== undefined && call.agentId !== '' && call.agentName !== undefined && call.agentName !== '') {
        agentsMap.set(call.agentId, call.agentName);
      }
    });
    return Array.from(agentsMap.entries()).map(([id, name]) => ({ id, name }));
  }, [sessionDetails?.calls]);

  // Get unique actions for filter
  const uniqueActions = useMemo(() => {
    if (sessionDetails?.calls === undefined || sessionDetails.calls === null) {
      return [];
    }
    const actions = new Set<string>();
    sessionDetails.calls.forEach((call) => {
      if (call.action != null) {
        actions.add(call.action);
      }
    });
    return Array.from(actions).sort();
  }, [sessionDetails?.calls]);

  // Filter calls
  const filteredCalls = useMemo(() => {
    if (sessionDetails?.calls === undefined || sessionDetails.calls === null) {
      return [];
    }

    let calls = [...sessionDetails.calls];

    // Apply tab filter
    switch (callsFilter) {
    case 'connected':
      calls = calls.filter(
        (c) => c.action === 'transferred' || c.action === 'answered'
      );
      break;
    case 'meetings':
      calls = calls.filter((c) => c.disposition === 'set_appointment');
      break;
    case 'recordings':
      calls = calls.filter((c) => c.existRecordings);
      break;
    }

    // Apply search filter
    if (searchQuery !== '') {
      const query = searchQuery.toLowerCase();
      calls = calls.filter(
        (c) =>
          (c.contactName?.toLowerCase().includes(query) ?? false) ||
          (c.contactListName?.toLowerCase().includes(query) ?? false)
      );
    }

    // Apply disposition filter
    if (dispositionFilter !== 'all') {
      calls = calls.filter((c) => c.disposition === dispositionFilter);
    }

    // Apply agent filter
    if (agentFilter !== 'all') {
      calls = calls.filter((c) => c.agentId === agentFilter);
    }

    // Apply action filter
    if (actionFilter !== 'all') {
      calls = calls.filter((c) => c.action === actionFilter);
    }

    // Sort by createdAt descending
    calls.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    return calls;
  }, [sessionDetails?.calls, callsFilter, searchQuery, dispositionFilter, agentFilter, actionFilter]);

  // Load recordings
  const loadRecordings = useCallback(async (callId: string) => {
    if (currentUserId === '') {
      return;
    }

    latestRecordingCallIdRef.current = callId;
    setRecordingsLoadingState('LOADING');
    try {
      const response = await dashboardApi.getRecordingsAsync(callId, currentUserId);

      // Guard: discard if a newer request was made while this one was in flight
      if (latestRecordingCallIdRef.current !== callId) {
        return;
      }

      if (response.success && response.data !== undefined) {
        setSelectedRecordings(response.data);
        setRecordingsLoadingState('SUCCESS');
      } else {
        setRecordingsLoadingState('ERROR');
      }
    } catch (error) {
      if (latestRecordingCallIdRef.current === callId) {
        setRecordingsLoadingState('ERROR');
      }
      void logError('Failed to load recordings', 'useSessionDetailsViewModel', { error, callId }, false);
    }
  }, [currentUserId, dashboardApi]);

  const closeRecordings = useCallback(() => {
    setSelectedRecordings(null);
    setRecordingsLoadingState('IDLE');
  }, []);

  const goBack = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const downloadReport = useCallback(async (type: ReportType) => {
    if (sessionId === undefined) {
      return;
    }

    setIsDownloadingReport(true);
    try {
      if (type === 'original') {
        // TODO: Implement original CSV download (requires contactListId lookup)
        // For now, fall back to compact
        await dashboardApi.downloadReportAsync(
          sessionId,
          'compact',
          sessionDetails?.session.organizationId
        );
      } else {
        await dashboardApi.downloadReportAsync(
          sessionId,
          type,
          type === 'raw' ? sessionDetails?.session.organizationId : undefined
        );
      }
    } catch (error) {
      void logError('Failed to download report', 'useSessionDetailsViewModel', { error, sessionId, type }, false);
    } finally {
      setIsDownloadingReport(false);
    }
  }, [sessionId, sessionDetails?.session.organizationId, dashboardApi]);

  return {
    // Data
    sessionDetails,
    filteredCalls,
    stats,
    selectedRecordings,
    totalContacts,
    flaggedContactsCount,

    // Filters
    callsFilter,
    setCallsFilter,
    searchQuery,
    setSearchQuery,
    dispositionFilter,
    setDispositionFilter,
    agentFilter,
    setAgentFilter,
    actionFilter,
    setActionFilter,
    setOnClickFun,

    // Loading
    loadingState,
    recordingsLoadingState,

    // Computed
    uniqueDispositions,
    uniqueAgents,
    uniqueActions,

    // Actions
    loadRecordings,
    closeRecordings,
    goBack,
    downloadReport,
    isDownloadingReport,
  };
}
